import React from 'react';
import { connect } from 'react-redux';
import AgentBuilder from './AgentBuilder';
import { I } from '../../../common/v5/config';
import {
  loadAgents,
  loadTools,
  loadWorkflows,
  createAgent,
  updateAgent,
  deleteAgent,
  associateToolWithAgent,
  removeToolFromAgent
} from '../../../redux/actions/async/agentBuilder';
import { setAdminData } from '../../../redux/actions/async/admin';
import { deleteToolManager } from '../../../redux/actions/async/ajax';
import { toggleAdminEdit, setAdminInput } from '../../../redux/actions/admin';
import { M_LLM_TOOL_MANAGER } from '../../../common/v5/constants';
import { onlyActiveAreasSelector } from '../../../redux/selectors/workflow';
import { onceAgentAreas } from '../../../redux/actions/async/workflow';

const AgentBuilderCtnr = ({
  // Redux state
  agents,
  tools,
  loading,
  error,
  
  // Redux actions
  loadAgents,
  loadTools,
  loadAgentAreas,
  createAgent,
  updateAgent,
  deleteAgent,
  associateToolWithAgent,
  removeToolFromAgent,
  setAdminData,
  setAdminInput,
  toggleAdminEdit,

  dispatch,
  
  // Props
  currentUser,
  ...props
}) => {

  const handleCreateAgent = (agentData) => {
    return createAgent(agentData, {});
  };

  const handleUpdateAgent = (agentData) => {
    return updateAgent(agentData, {});
  };

  const handleDeleteAgent = (agentId) => {
    return deleteAgent(agentId, {});
  };

  const handleAssociateTool = (agentId, toolName) => {
    return associateToolWithAgent(agentId, toolName, {});
  };

  const handleRemoveTool = (agentId, toolName) => {
    return removeToolFromAgent(agentId, toolName, {});
  };

  // Tool management handlers using real admin API
  const handleCreateTool = async (toolData) => {
    console.log('Create tool with data:', toolData);
    
    try {
      // Validate required fields
      if (!toolData.toolName || !toolData.toolDescription || !toolData.selectedArea) {
        throw new Error('Tool name, description, and area are required');
      }

      // Ensure parameters is an array
      let parameters = [];
      if (toolData.parameters) {
        if (Array.isArray(toolData.parameters)) {
          parameters = toolData.parameters;
        } else {
          console.warn('Parameters is not an array:', toolData.parameters);
        }
      }

      console.log('Setting admin input data for tool creation');
      
      // Set the admin input data in Redux state first
      setAdminInput('toolName', toolData.toolName);
      setAdminInput('toolDescription', toolData.toolDescription);
      setAdminInput('selectedArea', toolData.selectedArea);
      setAdminInput('parameters', parameters);
      setAdminInput('resp_text', toolData.resp_text || '');
      
      // Set the admin view to tool manager with ID 0 for new tool
      toggleAdminEdit(0, true);

      console.log('Calling setAdminData for tool creation');

      // Use the admin save mechanism for tool manager (create = no ID)
      const result = await setAdminData(M_LLM_TOOL_MANAGER, true, 0);
      
      // Check if the result contains an error even with 200 status
      if (result && result.error) {
        throw new Error(result.error);
      }
      
      console.log('Tool created successfully');
      
      // Small delay to ensure backend has processed the save
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh tools list - force reload from backend
      await loadTools();
      
      // Show success toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'success', icon: 'icon-check' },
          position: 'top-right',
          text: `Tool "${toolData.toolName}" created successfully`
        }
      });
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error creating tool:', error);
      
      // Show user-friendly toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to create tool: ${error.message || 'Unknown error'}`
        }
      });
      
      throw error;
    }
  };

  const handleUpdateTool = async (toolData) => {
    console.log('Update tool with data:', toolData);
    
    try {
      // Validate required fields
      if (!toolData.toolName || !toolData.toolDescription || !toolData.selectedArea) {
        throw new Error('Tool name, description, and area are required');
      }

      // Get the tool ID - try to get numeric ID from originalData first
      let toolId = toolData.id || toolData.tool_id;
      
      // If the ID is a string (like tool name), try to get numeric ID from originalData
      if (toolData.originalData && toolData.originalData.id) {
        toolId = toolData.originalData.id;
      }
      
      // Convert string ID to number if possible
      if (typeof toolId === 'string' && !isNaN(toolId)) {
        toolId = parseInt(toolId, 10);
      }
      
      console.log('Tool ID for update:', toolId, 'Type:', typeof toolId);
      
      if (!toolId) {
        throw new Error('Tool ID is required for update');
      }
      
      // If toolId is still a string, this means we're trying to update a tool that doesn't have a numeric ID
      // In this case, we should create a new tool instead
      if (typeof toolId === 'string') {
        console.log('Tool ID is a string, creating new tool instead of updating');
        return handleCreateTool(toolData);
      }

      // Ensure parameters is an array
      let parameters = [];
      if (toolData.parameters) {
        if (Array.isArray(toolData.parameters)) {
          parameters = toolData.parameters;
        } else {
          console.warn('Parameters is not an array:', toolData.parameters);
        }
      }

      console.log('Setting admin input data for tool update');
      
      // Set the admin input data in Redux state first
      setAdminInput('toolName', toolData.toolName);
      setAdminInput('toolDescription', toolData.toolDescription);
      setAdminInput('selectedArea', toolData.selectedArea);
      setAdminInput('parameters', parameters);
      setAdminInput('resp_text', toolData.resp_text || '');
      
      // Set the admin view to tool manager with the tool ID
      toggleAdminEdit(toolId, true);

      console.log('Calling setAdminData for tool update with ID:', toolId);

      // Use the admin save mechanism for tool manager (update = with ID)
      const result = await setAdminData(M_LLM_TOOL_MANAGER, false, toolId);
      
      // Check if the result contains an error even with 200 status
      if (result && result.error) {
        throw new Error(result.error);
      }
      
      console.log('Tool updated successfully');
      
      // Small delay to ensure backend has processed the save
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh tools list - force reload from backend
      await loadTools();
      
      // Show success toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'success', icon: 'icon-check' },
          position: 'top-right',
          text: `Tool "${toolData.toolName}" updated successfully`
        }
      });
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating tool:', error);
      
      // Show user-friendly toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to update tool: ${error.message || 'Unknown error'}`
        }
      });
      
      throw error;
    }
  };

  const handleDeleteTool = async (toolId) => {
    console.log('Delete tool with ID:', toolId);
    
    try {
      // Get the numeric tool ID
      let numericToolId = toolId;
      
      // Convert string ID to number if possible
      if (typeof toolId === 'string' && !isNaN(toolId)) {
        numericToolId = parseInt(toolId, 10);
      }
      
      console.log('Tool ID for deletion:', numericToolId, 'Type:', typeof numericToolId);
      
      if (!numericToolId || typeof numericToolId !== 'number') {
        throw new Error('Valid numeric tool ID is required for deletion');
      }
      
      // Use direct deleteToolManager call
      const result = await dispatch(deleteToolManager({ id: numericToolId }));
      
      // Check if the result contains an error even with 200 status
      if (result && result.error) {
        throw new Error(result.error);
      }
      
      console.log('Tool deleted successfully');
      
      // Small delay to ensure backend has processed the deletion
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh tools list - force reload from backend
      await loadTools();
      
      // Show success toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'success', icon: 'icon-check' },
          position: 'top-right',
          text: I('Tool deleted successfully')
        }
      });
      
    } catch (error) {
      console.error('Error deleting tool:', error);
      
      // Show error toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to delete tool: ${error.message || 'Unknown error'}`
        }
      });
      
      throw error;
    }
  };

  return (
    <AgentBuilder
      {...props}
      agents={agents}
      tools={tools}
      loading={loading}
      error={error}
      loadAgents={loadAgents}
      loadTools={loadTools}
      loadAgentAreas={loadAgentAreas}
      onCreateAgent={handleCreateAgent}
      onUpdateAgent={handleUpdateAgent}
      onDeleteAgent={handleDeleteAgent}
      onAssociateTool={handleAssociateTool}
      onRemoveTool={handleRemoveTool}
      onCreateTool={handleCreateTool}
      onUpdateTool={handleUpdateTool}
      onDeleteTool={handleDeleteTool}
    />
  );
};

const mapStateToProps = (state) => {
  console.log('Container mapStateToProps - full state:', state);
  console.log('Container mapStateToProps - state.app:', state.app);
  console.log('Container mapStateToProps - state.app.agentBuilder:', state.app.agentBuilder);
  
  const agentBuilderState = state.app?.agentBuilder || {};
  
  console.log('Container mapStateToProps - agentBuilderState:', agentBuilderState);
  
  console.log('Container mapStateToProps - state.app.workflow:', state.app.workflow);
  console.log('Container mapStateToProps - state.app.workflow.agentAreas:', state.app.workflow?.agentAreas);
  
  const areaList = onlyActiveAreasSelector(state);
  console.log('Container mapStateToProps - areaList from selector:', areaList);
  
  const mappedProps = {
    agents: agentBuilderState.agents || [],
    tools: agentBuilderState.tools || [],
    workflows: agentBuilderState.workflows || [],
    loading: agentBuilderState.loading || false,
    error: agentBuilderState.error || null,
    areaList: areaList,
    currentUser: state.app?.user?.currentUser
  };
  
  console.log('Container mapStateToProps - mapped props:', mappedProps);
  
  return mappedProps;
};

const mapDispatchToProps = (dispatch) => ({
  loadAgents: () => dispatch(loadAgents()),
  loadTools: () => dispatch(loadTools()),
  loadWorkflows: () => dispatch(loadWorkflows()),
  createAgent: (data) => dispatch(createAgent(data)),
  updateAgent: (data) => dispatch(updateAgent(data)),
  deleteAgent: (id) => dispatch(deleteAgent(id)),
  associateToolWithAgent: (agentId, toolName) => dispatch(associateToolWithAgent(agentId, toolName)),
  removeToolFromAgent: (agentId, toolName) => dispatch(removeToolFromAgent(agentId, toolName)),
  setAdminData: (...args) => dispatch(setAdminData(...args)),
  setAdminInput: (...args) => dispatch(setAdminInput(...args)),
  toggleAdminEdit: (...args) => dispatch(toggleAdminEdit(...args)),

  loadAgentAreas: () => dispatch(onceAgentAreas()),
  dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(AgentBuilderCtnr); 